import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonContent, IonIcon, IonLabel, IonButton } from '@ionic/angular';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { addIcons } from 'ionicons';
import { lockClosedOutline } from 'ionicons/icons';
import { filter } from 'rxjs';

import {
  HomePageToolbarComponent,
  UserMenuComponent,
  CompanySelectorMenuComponent,
} from '@ninety/mobile/platform/components';
import { APP_ROUTES, appActions, CurrentUserSelectors, selectFreeTierStatus } from '@ninety/mobile/platform/state';

@Component({
  selector: 'nty-freemium-page',
  imports: [
    HomePageToolbarComponent,
    UserMenuComponent,
    CompanySelectorMenuComponent,
    LetDirective,
    IonContent,
    IonIcon,
    IonLabel,
    IonButton,
  ],
  template: `
    <ion-content [id]="contentId" *ngrxLet="isAdminOrOwner$ as isAdminOrOwner">
      <nty-home-page-toolbar />

      <div class="freemium-message">
        <ion-icon class="lock-icon" name="lock-closed-outline" />
        <ion-label class="terra-title center">
          This feature on Ninety Mobile is not available on your current plan.
        </ion-label>
        <ion-label class="terra-title-small center">
          @if (isAdminOrOwner) {
            Upgrade your plan to access enhanced functionality to help you get aligned, get accountable, and grow your
            business.
          } @else {
            Contact your admin to upgrade your plan to access enhanced functionality to help you get aligned, get
            accountable, and grow your business.
          }
        </ion-label>

        @if (isAdminOrOwner) {
          <ion-button class="explore-plans-button" fill="outline" (click)="explorePlans()">
            <ion-icon src="assets/icons/lightning--fill.svg" slot="start" />
            Explore plans
          </ion-button>
        }
      </div>
    </ion-content>
    <nty-company-selector-menu [contentId]="contentId" />
    <nty-user-menu [contentId]="contentId" />
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      @use 'terra';

      ion-content {
        --background: #{terra.color(background, heavy)};
      }

      .freemium-message {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 24px;
        margin: 40% auto 0;
        max-width: 480px;
        text-align: center;
        color: terra.color(text);
      }

      .lock-icon {
        font-size: 48px;
        color: terra.color(text, light);
        margin-bottom: 8px;
      }

      .explore-plans-button {
        margin-top: 8px;
        height: 36px;
        --border-style: none;
        background: linear-gradient(90deg, #2f9787 33%, #2f8baa 100%);
        @include terra.radius(small);
        color: terra.color(white);
      }
    `,
  ],
})
export class FreemiumComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  contentId = 'freemium-content';
  isAdminOrOwner$ = this.store.select(CurrentUserSelectors.selectUserIsAdminOrOwner);

  constructor() {
    addIcons({ lockClosedOutline });
  }

  ngOnInit() {
    this.store
      .select(selectFreeTierStatus)
      .pipe(
        filter(({ isReady }) => isReady),
        filter(({ isFreeTier }) => !isFreeTier),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        window.location.href = `/${APP_ROUTES.App}/${APP_ROUTES.Home}`;
      });
  }

  explorePlans() {
    this.store.dispatch(appActions.openDesktopApp({ url: 'https://www.ninety.io/ninety-pricing' }));
  }
}
